import { useEffect, useState } from "react";
import JsBarcode from "jsbarcode";
import { Loader2, Pencil, Plus, Printer, ScanLine, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarcodeScanner } from "@/components/colis/BarcodeScanner";
import { OPTIONS_DEPOT } from "@/lib/barcode";
import { api, errMsg } from "@/lib/api";
import { lireCode } from "@/lib/colisCode";
import { ChariotVisuel } from "./ChariotVisuel";

const TYPES = [
  ["petit", "Petit"],
  ["moyen", "Moyen"],
  ["grand", "Grand (2 étages)"],
];
const ETAGES_DE = { petit: [null], moyen: [null], grand: ["haut", "bas"] };
const codeAuto = (lettre, etage) => `CHA-${(lettre || "X").toUpperCase()}${etage ? `-${etage === "haut" ? "H" : "B"}` : ""}`;

/** Étiquettes à imprimer : un code-barres (Code 128) par chariot / étage. */
export function imprimerEtiquettes(chariots) {
  const blocs = [];
  chariots.forEach((ch) => ch.emplacements.forEach((e) => {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    JsBarcode(svg, e.code, { format: "CODE128", width: 2.4, height: 90, displayValue: true, fontSize: 18, margin: 8 });
    blocs.push(`<div class="etq"><div class="t">${e.nom || ch.nom}</div>${svg.outerHTML}</div>`);
  }));
  const w = window.open("", "_blank");
  if (!w) { toast.error("Autorisez les fenêtres (pop-up) pour imprimer les étiquettes."); return; }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Étiquettes chariots</title><style>
    body{font-family:system-ui,sans-serif;margin:12mm;display:grid;grid-template-columns:repeat(2,1fr);gap:8mm}
    .etq{border:1px dashed #999;padding:4mm;text-align:center;break-inside:avoid}
    .t{font-size:22px;font-weight:800;margin-bottom:2mm}
    svg{max-width:100%;height:auto}
    @media print{.etq{border-color:#ccc}}
  </style></head><body>${blocs.join("")}<script>window.onload=()=>window.print()<\/script></body></html>`);
  w.document.close();
}

function FormChariot({ chariot, onClose, onSaved }) {
  const [type, setType] = useState(chariot?.type || "grand");
  const [lettre, setLettre] = useState(chariot?.lettre || "");
  const [codes, setCodes] = useState(() => Object.fromEntries((chariot?.emplacements || []).map((e) => [e.etage || "_", e.code])));
  const [scanPour, setScanPour] = useState(null); // étage dont on scanne le code
  const [busy, setBusy] = useState(false);
  const etages = ETAGES_DE[type];

  // Codes proposés automatiquement d'après la lettre (modifiables, ou scannés
  // si les chariots ont déjà des étiquettes).
  const val = (et) => codes[et || "_"] ?? codeAuto(lettre, et);
  const setCode = (et, v) => setCodes((c) => ({ ...c, [et || "_"]: v.toUpperCase().replace(/\s/g, "") }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { type, lettre, emplacements: etages.map((et) => ({ etage: et, code: val(et) })) };
    try {
      if (chariot) await api.put(`/depot/chariots/${chariot.id}`, body);
      else await api.post("/depot/chariots", body);
      toast.success(`Chariot ${lettre.toUpperCase()} ${chariot ? "modifié" : "créé"}`);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="space-y-4 rounded-xl border border-border p-4" data-testid="form-chariot">
      <div className="flex items-center justify-between">
        <p className="font-semibold">{chariot ? `Modifier le chariot ${chariot.lettre}` : "Nouveau chariot"}</p>
        <button type="button" onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground" aria-label="Fermer"><X size={16} /></button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {TYPES.map(([t, l]) => (
          <button key={t} type="button" onClick={() => setType(t)} data-testid={`type-${t}`}
            className={`rounded-lg border p-2 flex flex-col items-center gap-1 text-xs ${type === t ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:bg-accent"}`}>
            <ChariotVisuel type={t} lettre="" emplacements={[]} compteurs={false} taille={0.45} />
            {l}
          </button>
        ))}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="ch-lettre">Lettre *</Label>
        <Input id="ch-lettre" value={lettre} onChange={(e) => setLettre(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3))}
          placeholder="A" className="w-24 text-lg font-bold font-mono" required data-testid="ch-lettre" />
      </div>
      {etages.map((et) => (
        <div key={et || "u"} className="space-y-1.5">
          <Label>Code-barres {et ? `de l'étage ${et}` : "du chariot"} *</Label>
          <div className="flex gap-2">
            <Input value={val(et)} onChange={(e) => setCode(et, e.target.value)} className="font-mono" data-testid={`ch-code-${et || "u"}`} />
            <Button type="button" variant="outline" onClick={() => setScanPour(et || "_")} title="Scanner une étiquette existante"><ScanLine size={15} /></Button>
          </div>
        </div>
      ))}
      {scanPour && (
        <div className="space-y-2">
          <p className="text-sm">Scannez l'étiquette {scanPour === "_" ? "du chariot" : `de l'étage ${scanPour}`} :</p>
          <BarcodeScanner options={OPTIONS_DEPOT} accepter={(t) => !lireCode(t).type} aide="Visez l'étiquette du chariot"
            onDetected={(r) => { setCode(scanPour === "_" ? null : scanPour, r.text); setScanPour(null); }} onCancel={() => setScanPour(null)} />
        </div>
      )}
      <p className="text-xs text-muted-foreground">Code proposé d'après la lettre ; imprimez ensuite les étiquettes, ou scannez une étiquette déjà collée sur le chariot.</p>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
        <Button type="submit" disabled={!lettre || busy} data-testid="ch-enregistrer">{busy && <Loader2 size={14} className="animate-spin" />} {chariot ? "Enregistrer" : "Créer le chariot"}</Button>
      </div>
    </form>
  );
}

/** « Gérer les chariots » (administrateurs). */
export function GestionChariots({ open, onClose, onChange }) {
  const [chariots, setChariots] = useState([]);
  const [form, setForm] = useState(null); // { chariot | null }
  const [confirmDel, setConfirmDel] = useState(null);

  const charger = async () => {
    try { setChariots((await api.get("/depot/chariots")).data.chariots); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { if (open) { charger(); setForm(null); setConfirmDel(null); } }, [open]);

  const supprimer = async (ch) => {
    try { await api.delete(`/depot/chariots/${ch.id}`); toast.success(`Chariot ${ch.lettre} supprimé`); setConfirmDel(null); charger(); onChange?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92dvh] overflow-y-auto" data-testid="gestion-chariots">
        <DialogHeader>
          <DialogTitle>Gérer les chariots</DialogTitle>
          <DialogDescription>Chaque chariot a une lettre et un code-barres ; un grand chariot a un code par étage.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setForm({ chariot: null })} data-testid="btn-nouveau-chariot"><Plus size={14} /> Nouveau chariot</Button>
          {chariots.length > 0 && <Button size="sm" variant="outline" onClick={() => imprimerEtiquettes(chariots)} data-testid="btn-etiquettes"><Printer size={14} /> Imprimer les étiquettes</Button>}
        </div>
        {form && <FormChariot chariot={form.chariot} onClose={() => setForm(null)} onSaved={() => { setForm(null); charger(); onChange?.(); }} />}
        {chariots.length === 0 ? <p className="text-sm text-muted-foreground">Aucun chariot pour l'instant.</p> : (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {chariots.map((ch) => (
              <li key={ch.id} className="flex items-center gap-3 p-3" data-testid={`chariot-${ch.lettre}`}>
                <ChariotVisuel type={ch.type} lettre={ch.lettre} emplacements={ch.emplacements} compteurs={false} taille={0.4} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{ch.nom} <span className="text-xs font-normal text-muted-foreground">· {ch.type_label}</span></p>
                  <p className="text-xs text-muted-foreground font-mono">{ch.emplacements.map((e) => (e.etage ? `${e.etage} : ${e.code}` : e.code)).join(" · ")}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => imprimerEtiquettes([ch])} title="Imprimer ses étiquettes"><Printer size={14} /></Button>
                <Button size="sm" variant="ghost" onClick={() => setForm({ chariot: ch })} title="Modifier"><Pencil size={14} /></Button>
                {confirmDel === ch.id
                  ? <Button size="sm" variant="destructive" onClick={() => supprimer(ch)}>Supprimer ?</Button>
                  : <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirmDel(ch.id)} title="Supprimer"><Trash2 size={14} /></Button>}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
